import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, RotateCcw, Save } from "lucide-react";
import type { EmailTemplateKey } from "@finora/types";
import { ApiError } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

const TEMPLATE_LABELS: Record<EmailTemplateKey, { title: string; description: string }> = {
  ACCOUNT_CONFIRMATION: {
    title: "Potvrda naloga",
    description: 'Šalje se odmah nakon registracije - sadrži link "Potvrdi nalog" na kome korisnik postavlja lozinku.',
  },
  ACCOUNT_CONFIRMED: {
    title: "Nalog potvrđen",
    description: "Šalje se nakon što korisnik potvrdi nalog i postavi lozinku.",
  },
  PASSWORD_RESET: {
    title: "Reset lozinke",
    description: 'Šalje se na zahtev "Zaboravljena lozinka" - sadrži link za postavljanje nove lozinke.',
  },
  OTP_LOGIN: {
    title: "OTP kod za prijavu",
    description: 'Šalje se kad korisnik izabere tab "OTP kod" na prijavi - sadrži 6-cifreni kod.',
  },
  PACKAGE_SHIPPED: {
    title: "Paket je poslat",
    description: "Šalje se kupcu kad magacin pošalje paket kuriru - sadrži naziv kurirske službe i broj za praćenje pošiljke.",
  },
  SHIPMENT_STATUS: {
    title: "Promena statusa pošiljke",
    description: "Šalje se kupcu kad se status isporuke promeni (preuzeto, na dostavi, isporučeno, neuspela isporuka, otkazano) - samo ako je firma uključila tu opciju. Promenljive: {{status_title}}, {{status_text}}, {{courier_name}}, {{tracking_number}}, {{tracking_url}}, {{order_number}}, {{customer_name}}.",
  },
};

const TEMPLATE_KEYS: EmailTemplateKey[] = [
  "ACCOUNT_CONFIRMATION",
  "ACCOUNT_CONFIRMED",
  "PASSWORD_RESET",
  "OTP_LOGIN",
  "PACKAGE_SHIPPED",
  "SHIPMENT_STATUS",
];

/**
 * "HAfiscal Admin -> Email Template-i" - vidi razgovor "postoji u atminu
 * opcija admin templates gde bih mogao da menjam izgled". Lista tri
 * sistemska template-a levo, HTML editor + live preview (iframe, renderovan
 * preko backend /preview endpoint-a sa primer-podacima) desno. "Vrati na
 * podrazumevano" vraća HAfiscal mockup izgled (default-email-templates.ts).
 */
export function EmailTemplatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<EmailTemplateKey>("ACCOUNT_CONFIRMATION");
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [hydratedFor, setHydratedFor] = useState<EmailTemplateKey | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const { data: templates, isLoading } = useQuery({
    queryKey: ["email-templates"],
    queryFn: () => api.listEmailTemplates(),
  });

  const current = templates?.find((t) => t.key === selected);

  if (current && hydratedFor !== selected) {
    setSubject(current.subject);
    setHtmlBody(current.htmlBody);
    setHydratedFor(selected);
    setPreviewHtml(null);
  }

  const save = useMutation({
    mutationFn: () => api.updateEmailTemplate(selected, { subject, htmlBody }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["email-templates"] }),
  });

  const reset = useMutation({
    mutationFn: () => api.resetEmailTemplate(selected),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      setSubject(data.subject);
      setHtmlBody(data.htmlBody);
      setPreviewHtml(null);
    },
  });

  const preview = useMutation({
    mutationFn: () => api.previewEmailTemplate(selected),
    onSuccess: (data) => setPreviewHtml(data.html),
  });

  // Osveži preview kad se promeni odabrani template (ne na svaki keystroke - vidi dugme "Osveži pregled").
  useEffect(() => {
    setPreviewHtml(null);
  }, [selected]);

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/finora-admin/settings/email")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Nazad na Podešavanja / Email
      </button>

      <div>
        <h1 className="text-xl font-semibold text-slate-900">Email Template-i</h1>
        <p className="text-sm text-slate-500">
          Izmenite izgled sistemskih mailova. Placeholderi u dvostrukim vitičastim zagradama (npr.{" "}
          <code className="rounded bg-slate-100 px-1">{"{{confirmation_url}}"}</code>) se automatski popunjavaju
          pri slanju - ne brišite ih.
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="grid gap-4 md:grid-cols-[220px_1fr_1fr]">
          <div className="space-y-2">
            {TEMPLATE_KEYS.map((key) => (
              <button
                key={key}
                onClick={() => setSelected(key)}
                className={`w-full rounded-lg border p-3 text-left text-sm transition-colors ${
                  selected === key ? "border-brand bg-brand-light text-brand" : "border-border hover:bg-slate-50"
                }`}
              >
                <p className="font-medium">{TEMPLATE_LABELS[key].title}</p>
                <p className="mt-0.5 text-xs text-slate-500">{TEMPLATE_LABELS[key].description}</p>
              </button>
            ))}
          </div>

          <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <div className="mb-3">
              <label className="mb-1 block text-xs text-slate-500">Naslov mejla (subject)</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <label className="mb-1 block text-xs text-slate-500">HTML sadržaj</label>
            <textarea
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              spellCheck={false}
              className="h-[420px] w-full rounded-lg border border-border p-2 font-mono text-xs focus:border-brand focus:outline-none"
            />

            {save.isError && (
              <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
              </p>
            )}

            <div className="mt-3 flex items-center justify-between">
              <Button
                size="sm"
                variant="secondary"
                disabled={reset.isPending}
                onClick={() => {
                  if (confirm("Vratiti ovaj template na HAFiskal podrazumevani izgled? Trenutne izmene se gube.")) {
                    reset.mutate();
                  }
                }}
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                Vrati na podrazumevano
              </Button>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" disabled={preview.isPending} onClick={() => preview.mutate()}>
                  <Eye className="h-3.5 w-3.5" aria-hidden />
                  Osveži pregled
                </Button>
                <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
                  <Save className="h-3.5 w-3.5" aria-hidden />
                  {save.isPending ? "Čuvam..." : "Sačuvaj"}
                </Button>
              </div>
            </div>
            {current && (
              <p className="mt-2 text-xs text-slate-400">
                Poslednja izmena: {new Date(current.updatedAt).toLocaleString("sr-RS")}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border bg-surface shadow-subtle">
            <div className="border-b border-border px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-400">
              Pregled (sa primer-podacima)
            </div>
            {previewHtml ? (
              <iframe
                title="Pregled email template-a"
                srcDoc={previewHtml}
                sandbox=""
                className="h-[460px] w-full rounded-b-xl bg-white"
              />
            ) : (
              <div className="flex h-[460px] items-center justify-center p-4 text-center text-sm text-slate-400">
                Klikni "Osveži pregled" da vidiš kako mejl izgleda sa primer-podacima.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
